import PrivateRouteLayout from "@components/layouts/PrivateRouteLayout";
import PublicRouteLayout from "@components/layouts/PublicRouteLayout";
import RootRouteLayout from "@components/layouts/RootRouteLayout";
import WSRouteLayout from "@components/layouts/WSRouteLayout";
import AppUpdateOnRouting from "@components/utils/AppUpdateOnRouting";
import DailyReferBallClaimOnRouting from "@components/utils/DailyReferBallClaimOnRouting/DailyReferBallClaimOnRouting";
import FastLoginHandler from "@components/utils/FastLoginHandler/FastLoginHandler";
import { LazyLoadComponent } from "@components/utils/LazyLoadComponent";
import MarketingDataCollector from "@components/utils/MarketingDataCollector";
import NavigateWithSearchParams from "@components/utils/NavigateWithSearchParams";
import Toaster from "@components/utils/Toaster";
import { PostHogPreloadProvider } from "@configs/post-hog/preload";
import { OnboardMode, Paths } from "@constants";
import * as Sentry from "@sentry/react";
import { UserMode } from "@types";
import { lazyWithPreload } from "@utils/lazyWithPreload";
import { SecretCodeSubpage } from "@views/PromotionSecretCodePage/types";
import { MemberSettingFundingMode } from "kz-ui-sdk";
import { Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

const LazyHomePage = lazyWithPreload(() => import("@views/HomePage"));
const LazyPrizesPage = lazyWithPreload(() => import("@views/PrizesPage"));
const LazyProfilePage = lazyWithPreload(() => import("@views/ProfilePage"));
const LazyDailyRewardPage = lazyWithPreload(() => import("@views/DailyRewardPage"));
const LazyCashbackPage = lazyWithPreload(() => import("@views/CashbackPage"));
const LazyTransactionPage = lazyWithPreload(() => import("@views/TransactionPage"));
const LazyLanguagePage = lazyWithPreload(() => import("@views/LanguagePage"));
const LazyBetHistoryPage = lazyWithPreload(() => import("@views/BetHistoryPage"));
const LazyTermsConditionsPage = lazyWithPreload(() => import("@views/TNCPage"));
const LazyReferralPage = lazyWithPreload(() => import("@views/ReferralPage"));
const DepositCallbackPage = lazyWithPreload(() => import("@views/DepositCallbackPage"));
const LazyReferBallsPage = lazyWithPreload(() => import("@views/ReferBallsPage"));
const LazyReferralCallbackPage = lazyWithPreload(() => import("@views/ReferralCallbackPage"));
const LazyMaintenancePage = lazyWithPreload(() => import("@views/MaintenancePage"));
const LazyChangePasswordPage = lazyWithPreload(() => import("@views/ChangePasswordPage"));
const LazyIframeShutdownPage = lazyWithPreload(() => import("@views/IframeShutdownPage"));
const LazyBankAccountPage = lazyWithPreload(() => import("@views/BankAccountPage"));
const LazyPersonalInfoPage = lazyWithPreload(() => import("@views/PersonalInfoPage"));
const LazyChangePinPage = lazyWithPreload(() => import("@views/ChangePinPage"));
const LazyOnboardingPage = lazyWithPreload(() => import("@views/OnboardingPage"));
const LazyAuthBankAccountPage = lazyWithPreload(() => import("@views/AuthBankAccountPage"));
const LazyAuthBankLegalNamePage = lazyWithPreload(() => import("@views/AuthBankLegalNamePage"));
const LazyAuthLoginPage = lazyWithPreload(() => import("@views/AuthLoginPage"));
const LazyAuthResetPage = lazyWithPreload(() => import("@views/AuthResetPage"));
const LazyAuthLegalNamePage = lazyWithPreload(() => import("@views/AuthLegalNamePage"));
const LazyAuthRegisterPage = lazyWithPreload(() => import("@views/AuthRegisterPage"));
const LazyAuthForgotPage = lazyWithPreload(() => import("@views/AuthForgotPage"));
const LazyFundingProfilesPage = lazyWithPreload(() => import("@views/FundingProfilesPage"));
const LazyPromotionPlayAndSpinPage = lazyWithPreload(() => import("@views/PromotionPlayAndSpin"));
const LazyPromotionMegaSharePage = lazyWithPreload(() => import("@views/PromotionMegaSharePage"));
const LazyPromotionSecretCodePage = lazyWithPreload(() => import("@views/PromotionSecretCodePage"));

// Improve LCP by preloading the home page
LazyHomePage.preload();

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const AppRouter = () => {
  return (
    <Suspense>
      <BrowserRouter>
        <PostHogPreloadProvider>
          <Toaster />
          <MarketingDataCollector />
          <FastLoginHandler />
          <AppUpdateOnRouting />
          <DailyReferBallClaimOnRouting />

          <RootRouteLayout>
            <SentryRoutes>
              <Route element={<WSRouteLayout />}>
                {/*GUEST ROUTE*/}
                <Route element={<PrivateRouteLayout mode={UserMode.GUEST} />}>
                  <Route
                    path={Paths.PRIVATE.HOME}
                    element={
                      <LazyLoadComponent
                        preloadDelay={500}
                        preload={[
                          LazyPrizesPage,
                          LazyCashbackPage,
                          LazyDailyRewardPage,
                          LazyReferralPage,
                          LazyPromotionPlayAndSpinPage,
                        ]}
                      >
                        <LazyHomePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PRIZES}
                    element={
                      <LazyLoadComponent>
                        <LazyPrizesPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_PLAY_AND_SPIN}
                    element={
                      <LazyLoadComponent>
                        <LazyPromotionPlayAndSpinPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_CASHBACK}
                    element={
                      <LazyLoadComponent>
                        <LazyCashbackPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_DAILY_LOGIN}
                    element={
                      <LazyLoadComponent>
                        <LazyDailyRewardPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_REFER_FRIEND}
                    element={
                      <LazyLoadComponent>
                        <LazyReferralPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_REFER_BALLS}
                    element={
                      <LazyLoadComponent>
                        <LazyReferBallsPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_MEGA_SHARE}
                    element={
                      <LazyLoadComponent>
                        <Navigate
                          to={Paths.PRIVATE.PROMOTION_SHARE_AND_EARN}
                          replace
                        />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_SHARE_AND_EARN}
                    element={
                      <LazyLoadComponent>
                        <LazyPromotionMegaSharePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_SECRET_CODE}
                    element={
                      <LazyLoadComponent>
                        <LazyPromotionSecretCodePage subpage={SecretCodeSubpage.GIVER} />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PROMOTION_REDEEM_SECRET_CODE}
                    element={
                      <LazyLoadComponent>
                        <LazyPromotionSecretCodePage subpage={SecretCodeSubpage.TAKER} />
                      </LazyLoadComponent>
                    }
                  />
                </Route>

                {/*PRIVATE ROUTES*/}
                {/*PrivateRouteLayout is a layout that checks if the user is authenticated before rendering the children */}
                {/*The user is redirected to the home page if not authenticated*/}
                <Route element={<PrivateRouteLayout mode={UserMode.MEMBER} />}>
                  <Route
                    path={Paths.PRIVATE.PROFILE}
                    element={
                      <LazyLoadComponent>
                        <LazyProfilePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.TRANSACTION}
                    element={
                      <LazyLoadComponent>
                        <LazyTransactionPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.LANGUAGE}
                    element={
                      <LazyLoadComponent>
                        <LazyLanguagePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.BET_HISTORY}
                    element={
                      <LazyLoadComponent>
                        <LazyBetHistoryPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE}
                    element={
                      <LazyLoadComponent>
                        <LazyAuthBankAccountPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE}
                    element={
                      <LazyLoadComponent>
                        <LazyAuthLegalNamePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE}
                    element={
                      <LazyLoadComponent>
                        <LazyAuthBankLegalNamePage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.AUTH_RESET_PAGE}
                    element={
                      <LazyLoadComponent>
                        <LazyAuthResetPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.CHANGE_PASSWORD}
                    element={
                      <LazyLoadComponent>
                        <LazyChangePasswordPage />
                      </LazyLoadComponent>
                    }
                  />

                  <Route
                    path={Paths.PRIVATE.CHANGE_PIN}
                    element={
                      <LazyLoadComponent>
                        <LazyChangePinPage />
                      </LazyLoadComponent>
                    }
                  />

                  <Route
                    path={Paths.PRIVATE.BANK_ACCOUNT}
                    element={
                      <LazyLoadComponent>
                        <LazyBankAccountPage />
                      </LazyLoadComponent>
                    }
                  />

                  <Route
                    path={Paths.PRIVATE.BANK_ACCOUNTS}
                    element={
                      <LazyLoadComponent>
                        <LazyFundingProfilesPage defaultFundingMode={MemberSettingFundingMode.BankOnly} />
                      </LazyLoadComponent>
                    }
                  />

                  <Route
                    path={Paths.PRIVATE.E_WALLETS}
                    element={
                      <LazyLoadComponent>
                        <LazyFundingProfilesPage defaultFundingMode={MemberSettingFundingMode.EWalletOnly} />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.PERSONAL_INFO}
                    element={
                      <LazyLoadComponent>
                        <LazyPersonalInfoPage />
                      </LazyLoadComponent>
                    }
                  />
                  <Route
                    path={Paths.PRIVATE.TERMS_CONDITIONS}
                    element={
                      <LazyLoadComponent>
                        <LazyTermsConditionsPage />
                      </LazyLoadComponent>
                    }
                  />
                </Route>
              </Route>

              {/* CALL BACK ROUTES */}
              {/**
               * These routes are used to handle the callback from other sites
               */}
              <Route
                path={Paths.PRIVATE.DEPOSIT_CALLBACK}
                element={
                  <LazyLoadComponent>
                    <DepositCallbackPage />
                  </LazyLoadComponent>
                }
              />
              <Route
                path={Paths.PUBLIC.IFRAME_SHUTDOWN}
                element={
                  <LazyLoadComponent>
                    <LazyIframeShutdownPage />
                  </LazyLoadComponent>
                }
              />

              {/*PUBLIC ROUTES*/}
              <Route element={<PublicRouteLayout />}>
                {/*ONBOARDING ROUTES*/}

                {/* /onboard/login will redirect to home, open the drawer > land on login tab */}
                <Route
                  path={Paths.PUBLIC.ONBOARD_LOGIN_DIALOG}
                  element={
                    <NavigateWithSearchParams
                      to={Paths.PRIVATE.HOME}
                      replace
                    />
                  }
                />

                {/* /onboard/register will redirect to home, open the drawer > land on register tab */}
                <Route
                  path={Paths.PUBLIC.ONBOARD_REGISTER_DIALOG}
                  element={
                    <NavigateWithSearchParams
                      to={Paths.PRIVATE.HOME}
                      replace
                    />
                  }
                />

                {/* /onboard will redirect to home, open the drawer > land on register tab */}
                <Route
                  path={Paths.PUBLIC.ONBOARD_DIALOG}
                  element={
                    <NavigateWithSearchParams
                      to={Paths.PRIVATE.HOME}
                      replace
                    />
                  }
                />

                {/* register will open full onboarding page */}
                <Route
                  path={Paths.PUBLIC.ONBOARD_REGISTER_PAGE}
                  element={
                    <LazyLoadComponent>
                      <LazyOnboardingPage defaultMode={OnboardMode.REGISTER} />
                    </LazyLoadComponent>
                  }
                />

                {/* login will open full onboarding page */}
                <Route
                  path={Paths.PUBLIC.ONBOARD_LOGIN_PAGE}
                  element={
                    <LazyLoadComponent>
                      <LazyOnboardingPage defaultMode={OnboardMode.LOGIN} />
                    </LazyLoadComponent>
                  }
                />

                {/* login full page */}
                <Route
                  path={Paths.PUBLIC.AUTH_LOGIN_PAGE}
                  element={
                    <LazyLoadComponent
                      preloadDelay={500}
                      preload={[LazyHomePage]}
                    >
                      <LazyAuthLoginPage />
                    </LazyLoadComponent>
                  }
                />

                {/* register full page*/}
                <Route
                  path={Paths.PUBLIC.AUTH_REGISTER_PAGE}
                  element={
                    <LazyLoadComponent>
                      <LazyAuthRegisterPage />
                    </LazyLoadComponent>
                  }
                />

                {/* forgot pin full page*/}
                <Route
                  path={Paths.PUBLIC.AUTH_FORGOT_PAGE}
                  element={
                    <LazyLoadComponent>
                      <LazyAuthForgotPage />
                    </LazyLoadComponent>
                  }
                />

                <Route
                  path={Paths.PUBLIC.REFERRAL_CALLBACK}
                  element={
                    <LazyLoadComponent>
                      <LazyReferralCallbackPage />
                    </LazyLoadComponent>
                  }
                />

                <Route
                  path={Paths.PUBLIC.MAINTENANCE}
                  element={
                    <LazyLoadComponent>
                      <LazyMaintenancePage />
                    </LazyLoadComponent>
                  }
                />
              </Route>

              {/* 404 Route - Catch all unknown routes */}
              <Route
                path="*"
                element={
                  <Navigate
                    to={Paths.PRIVATE.HOME}
                    replace
                  />
                }
              />
            </SentryRoutes>
          </RootRouteLayout>
        </PostHogPreloadProvider>
      </BrowserRouter>
    </Suspense>
  );
};

export default AppRouter;
